import { useMemo, useState, useCallback, useEffect, useRef } from "react";
import {
  calculateSeniorPacket,
  calculateScholarshipEligible,
  calculateScholarshipStatus,
  getSeniorPacketStatusText,
} from "../utils/scholarshipUtils";

/**
 * Custom hook for filtering data based on filter configuration
 *
 * @param {Array} data - The data to filter
 * @param {Array} filterConfig - Filter configuration array
 * @returns {Object} Object containing filteredData and filter management functions
 */
const useCustomFilter = (data, filterConfig) => {
  const [filterModel, setFilterModel] = useState({});
  const [filteredData, setFilteredData] = useState(
    Array.isArray(data) ? data : [],
  );
  const [isFiltering, setIsFiltering] = useState(false);
  const pendingRef = useRef(null);

  const computeFilteredData = useCallback(() => {
    if (!data || !Array.isArray(data)) return [];
    if (!filterModel || Object.keys(filterModel).length === 0) return data;

    return data.filter((item) => {
      // Handle global search field separately
      const globalSearch = filterModel.search;
      if (globalSearch && globalSearch.trim() !== "") {
        const searchTerm = globalSearch.toLowerCase();
        const matchesGlobalSearch = Object.values(item).some((value) => {
          if (value == null) return false;
          return value.toString().toLowerCase().includes(searchTerm);
        });
        if (!matchesGlobalSearch) return false;
      }

      // Apply specific field filters
      return filterConfig.every((field) => {
        // Skip the global search field as it's handled above
        if (field.name === "search") return true;

        const filterValue = filterModel[field.name];
        let itemValue = item[field.name];

        // Handle calculated fields that need special processing
        if (field.customFilter === true) {
          if (field.name === "senior_packet") {
            // Calculate the senior packet status text on-the-fly
            itemValue = getSeniorPacketStatusText(item) || "Not Applicable";
          } else if (field.name === "scholarship_eligible") {
            // Calculate the scholarship status text on-the-fly
            itemValue = calculateScholarshipStatus(item) || "Not Applicable";
          }
        }

        // Skip if no filter value is set
        if (
          filterValue === null ||
          filterValue === undefined ||
          filterValue === ""
        ) {
          return true;
        }

        switch (field.type) {
          case "text":
            return itemValue
              ?.toString()
              .toLowerCase()
              .includes(filterValue.toLowerCase());

          case "select":
          case "boolean":
            if (field.type === "boolean") {
              // For boolean fields, normalize the item value using the provided normalizer or default logic
              const normalizedValue = field.valueNormalizer
                ? field.valueNormalizer(itemValue)
                : getBooleanDisplayValue(itemValue);
              return normalizedValue === filterValue;
            }

            // Handle calculated fields flagged with customFilter: true
            if (field.customFilter === true) {
              if (field.name === "senior_packet") {
                itemValue = getSeniorPacketStatusText(item) || "Not Applicable";
              } else if (field.name === "scholarship_eligible") {
                itemValue =
                  calculateScholarshipStatus(item) || "Not Applicable";
              }
              return itemValue === filterValue;
            }

            // Check if there's a custom filter function
            if (
              field.customFilter &&
              typeof field.customFilter === "function"
            ) {
              return field.customFilter(itemValue, filterValue, field.options);
            }

            return itemValue === filterValue;

          case "multiselect":
            if (!Array.isArray(filterValue) || filterValue.length === 0)
              return true;
            // Check if item value matches any of the selected values
            return filterValue.some((selectedValue) => {
              const selectedVal = selectedValue.value || selectedValue;
              return (
                itemValue === selectedVal ||
                itemValue?.toString().includes(selectedVal)
              );
            });

          case "switch":
            // For switch filters, only filter when the switch is enabled
            // When enabled (true), show only items that match the switch state
            return filterValue === true ? itemValue === true : true;

          case "date":
            if (!filterValue) return true;
            const itemDate = itemValue?.toDate
              ? itemValue.toDate()
              : new Date(itemValue);
            const filterDate = new Date(filterValue);
            // Compare dates (ignoring time)
            return itemDate.toDateString() === filterDate.toDateString();

          case "daterange":
            if (!filterValue.start && !filterValue.end) return true;
            const itemDateForRange = itemValue?.toDate
              ? itemValue.toDate()
              : new Date(itemValue);

            if (filterValue.start && filterValue.end) {
              return (
                itemDateForRange >= filterValue.start &&
                itemDateForRange <= filterValue.end
              );
            } else if (filterValue.start) {
              return itemDateForRange >= filterValue.start;
            } else if (filterValue.end) {
              return itemDateForRange <= filterValue.end;
            }
            return true;

          case "number":
            const numValue = parseFloat(itemValue);
            const filterNumValue = parseFloat(filterValue);
            return (
              !isNaN(numValue) &&
              !isNaN(filterNumValue) &&
              numValue === filterNumValue
            );

          case "numberrange":
            if (!filterValue.min && !filterValue.max) return true;
            const itemNumValue = parseFloat(itemValue);
            if (isNaN(itemNumValue)) return false;

            if (filterValue.min && filterValue.max) {
              return (
                itemNumValue >= parseFloat(filterValue.min) &&
                itemNumValue <= parseFloat(filterValue.max)
              );
            } else if (filterValue.min) {
              return itemNumValue >= parseFloat(filterValue.min);
            } else if (filterValue.max) {
              return itemNumValue <= parseFloat(filterValue.max);
            }
            return true;

          default:
            return true;
        }
      });
    });
  }, [data, filterModel, filterConfig]);

  // Filtering runs on a deferred tick (rather than synchronously in a
  // render-time useMemo) so the "isFiltering" flag actually has a chance to
  // paint before the (possibly expensive, e.g. ~99k-row Students) filter
  // pass runs — that's what powers the search progress indicator in
  // CustomFilterPanel.
  useEffect(() => {
    if (pendingRef.current) clearTimeout(pendingRef.current);

    const hasFilters = filterModel && Object.keys(filterModel).length > 0;

    if (!hasFilters) {
      setFilteredData(Array.isArray(data) ? data : []);
      setIsFiltering(false);
      return undefined;
    }

    setIsFiltering(true);
    pendingRef.current = setTimeout(() => {
      setFilteredData(computeFilteredData());
      setIsFiltering(false);
    }, 0);

    return () => {
      if (pendingRef.current) clearTimeout(pendingRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, filterModel, filterConfig]);

  const resetFilters = useCallback(() => {
    setFilterModel({});
  }, []);

  const hasActiveFilters = useMemo(() => {
    return Object.keys(filterModel).some(
      (key) =>
        filterModel[key] !== null &&
        filterModel[key] !== undefined &&
        filterModel[key] !== "",
    );
  }, [filterModel]);

  return {
    filteredData,
    filterModel,
    setFilterModel,
    resetFilters,
    hasActiveFilters,
    isFiltering,
  };
};

// Helper function for boolean value normalization (can be overridden per field)
const getBooleanDisplayValue = (value) => {
  if (value === -1 || value === "-1" || value === "Yes") return "Yes";
  if (value === 0 || value === "0" || value === "No") return "No";
  if (value === null || typeof value === "undefined") return "";
  return String(value);
};

export default useCustomFilter;
